/**
 * Genetic Gain manual-entry form — Breeder's equation calculator.
 *
 * Implements ΔG = i × h² × σp (Falconer & Mackay 1996, Ch. 11).
 * Standardised selection differential (i) lookup table follows
 * Falconer & Mackay 1996 Table 11.1 (truncation selection from a
 * normal distribution).
 *
 * Niranjan (Feb 2026) — bug fix: this card was previously empty
 * because DesignUploadCard hides the upload zone when noUpload=true,
 * but no manual-entry form rendered in its place. Plus the backend
 * route used Form(...) and ignored JSON. Both fixed.
 */
import React, { useState } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Calculator, BookOpen } from 'lucide-react';
import { parseAxiosError } from '@/utils/axiosErrors';

// Selection intensity (i) — standardised selection differential for
// a normally distributed population under truncation selection.
// Source: Falconer & Mackay (1996) "Introduction to Quantitative
// Genetics" 4th ed., Longman, Table 11.1.
const INTENSITY_OPTIONS = [
  { pct: '1', i: 2.67, label: '1% selected (i = 2.67)' },
  { pct: '5', i: 2.06, label: '5% selected (i = 2.06)' },
  { pct: '10', i: 1.76, label: '10% selected (i = 1.76)' },
  { pct: '20', i: 1.40, label: '20% selected (i = 1.40)' },
  { pct: '30', i: 1.16, label: '30% selected (i = 1.16)' },
  { pct: '50', i: 0.80, label: '50% selected (i = 0.80)' },
];

export default function GeneticGainForm({ onResult, onError, color = '#7C3AED' }) {
  const [h2, setH2] = useState('0.5');
  const [intensity, setIntensity] = useState('10');
  const [sigmaP, setSigmaP] = useState('1.0');
  const [genInterval, setGenInterval] = useState('1.0');
  const [target, setTarget] = useState('0');
  const [running, setRunning] = useState(false);

  const fillSample = () => {
    // Wheat grain yield example, Singh & Chaudhary (1985) p. 162:
    // h² = 0.45, σp = 4.2 q/ha, target = 8 q/ha gain in 5% intensity.
    setH2('0.45');
    setIntensity('5');
    setSigmaP('4.2');
    setGenInterval('1.0');
    setTarget('8');
    toast.info('Loaded Singh & Chaudhary (1985) wheat-yield example.');
  };

  const validate = () => {
    const errors = [];
    const h2v = parseFloat(h2);
    if (isNaN(h2v) || h2v <= 0 || h2v > 1)
      errors.push('Heritability (h²) must be a number between 0 and 1.');
    const sp = parseFloat(sigmaP);
    if (isNaN(sp) || sp <= 0)
      errors.push('Phenotypic standard deviation (σp) must be greater than 0.');
    const gi = parseFloat(genInterval);
    if (isNaN(gi) || gi <= 0)
      errors.push('Generation interval must be greater than 0.');
    const tg = parseFloat(target);
    if (isNaN(tg) || tg < 0)
      errors.push('Target gain cannot be negative (use 0 to skip).');
    return errors;
  };

  const handleRun = async () => {
    const errors = validate();
    if (errors.length) {
      toast.error(errors.join(' '));
      return;
    }
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/analyze/genetic-gain`,
        {
          h2: parseFloat(h2),
          intensity,
          sigma_p: parseFloat(sigmaP),
          gen_interval: parseFloat(genInterval),
          target: parseFloat(target),
        },
        { withCredentials: true },
      );
      toast.success('Genetic gain calculated.');
      if (onResult) onResult(data);
    } catch (e) {
      const msg = parseAxiosError(e);
      toast.error(msg);
      if (onError) onError(msg);
    } finally {
      setRunning(false);
    }
  };

  const fieldStyle = {
    width: '100%',
    fontSize: 14,
    padding: '10px 12px',
    border: '1.5px solid var(--border-color)',
    borderRadius: 'var(--radius-md)',
    background: 'white',
    color: 'var(--text-dark)',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  };
  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-medium)',
    marginBottom: 6,
    letterSpacing: '0.02em',
  };
  const helperStyle = {
    fontSize: 11,
    color: 'var(--text-light)',
    marginTop: 4,
    fontStyle: 'italic',
  };

  return (
    <div
      data-testid="genetic-gain-form"
      className="rounded-xl space-y-5"
      style={{
        border: `1.5px solid ${color}30`,
        background: `${color}06`,
        padding: 24,
      }}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className="flex items-center justify-center shrink-0"
            style={{
              width: 40, height: 40, borderRadius: 10,
              background: color, color: 'white',
            }}
          >
            <Calculator size={20} strokeWidth={1.6} />
          </div>
          <div>
            <h3
              className="font-heading"
              style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-dark)', margin: 0 }}
            >
              Genetic Gain — Breeder&rsquo;s Equation
            </h3>
            <p
              className="font-body"
              style={{ fontSize: 12, color: 'var(--text-medium)', margin: '2px 0 0 0' }}
            >
              ΔG = i × h² × σp &nbsp;·&nbsp; Falconer &amp; Mackay (1996), Ch. 11
            </p>
          </div>
        </div>
        <button
          onClick={fillSample}
          type="button"
          data-testid="genetic-gain-sample-btn"
          className="font-body btn-press"
          style={{
            fontSize: 12, fontWeight: 600,
            padding: '7px 14px', borderRadius: 'var(--radius-md)',
            border: `1.5px solid ${color}`,
            color: color, background: 'white',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <BookOpen size={12} /> Try Sample (Singh &amp; Chaudhary 1985)
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="gg-h2" style={labelStyle}>
            HERITABILITY (h²) — narrow or broad sense
          </label>
          <input
            id="gg-h2"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={h2}
            onChange={(e) => setH2(e.target.value)}
            style={fieldStyle}
            data-testid="genetic-gain-h2"
          />
          <p style={helperStyle}>
            Range 0&ndash;1. Typical values: 0.1 (low) &middot; 0.4 (moderate) &middot; 0.7+ (high).
          </p>
        </div>

        <div>
          <label htmlFor="gg-intensity" style={labelStyle}>
            SELECTION INTENSITY (% SELECTED)
          </label>
          <select
            id="gg-intensity"
            value={intensity}
            onChange={(e) => setIntensity(e.target.value)}
            style={{ ...fieldStyle, cursor: 'pointer' }}
            data-testid="genetic-gain-intensity"
          >
            {INTENSITY_OPTIONS.map((opt) => (
              <option key={opt.pct} value={opt.pct}>{opt.label}</option>
            ))}
          </select>
          <p style={helperStyle}>
            i value from Falconer &amp; Mackay (1996) Table 11.1 — truncation selection.
          </p>
        </div>

        <div>
          <label htmlFor="gg-sigma" style={labelStyle}>
            PHENOTYPIC SD (σp) — trait units
          </label>
          <input
            id="gg-sigma"
            type="number"
            step="0.01"
            min="0.0001"
            value={sigmaP}
            onChange={(e) => setSigmaP(e.target.value)}
            style={fieldStyle}
            data-testid="genetic-gain-sigma"
          />
          <p style={helperStyle}>
            Standard deviation of the trait in the source population (e.g. yield SD = 4.2 q/ha).
          </p>
        </div>

        <div>
          <label htmlFor="gg-gen" style={labelStyle}>
            GENERATION INTERVAL (years/cycle)
          </label>
          <input
            id="gg-gen"
            type="number"
            step="0.1"
            min="0.1"
            value={genInterval}
            onChange={(e) => setGenInterval(e.target.value)}
            style={fieldStyle}
            data-testid="genetic-gain-gen-interval"
          />
          <p style={helperStyle}>
            Years per breeding cycle. Used to derive ΔG / year.
          </p>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="gg-target" style={labelStyle}>
            TARGET GAIN (trait units, optional)
          </label>
          <input
            id="gg-target"
            type="number"
            step="0.01"
            min="0"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            style={fieldStyle}
            data-testid="genetic-gain-target"
          />
          <p style={helperStyle}>
            Set 0 to skip. Otherwise the response includes &lsquo;cycles to target&rsquo;.
          </p>
        </div>
      </div>

      <button
        onClick={handleRun}
        disabled={running}
        data-testid="genetic-gain-run-btn"
        className="w-full font-body btn-press transition-all disabled:opacity-50"
        style={{
          height: 52,
          borderRadius: 'var(--radius-md)',
          fontWeight: 600, fontSize: 16, letterSpacing: '0.02em',
          color: 'white',
          background: `linear-gradient(135deg, ${color}, ${color}D0)`,
          border: 'none',
        }}
      >
        {running ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Calculating&hellip;
          </span>
        ) : 'Calculate Genetic Gain'}
      </button>
    </div>
  );
}
